import { queryOptions, useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';

import { getAccessToken } from '@/lib/tokenStorage';

import {
  CommentResponse,
  CreatedFertResponse,
  CreateFertilizationRequest,
  CreateFertilizationResponse,
  FertilizationDonorScheduleResponse,
  FertPulledSemenResponse,
  FertSiteResponse,
  ScheduledDonorResponse,
  TechResponse,
  UpsertReverseSortRequest,
  UpsertReverseSortResponse,
} from './types';

export type {
  CommentResponse as CommentItem,
  CreateFertilizationRequest,
  CreateFertilizationResponse,
  CreatedFertResponse as CreatedFertItem,
  FertilizationDonorScheduleResponse as FertDonorScheduleItem,
  FertPulledSemenResponse as FertPulledSemenItem,
  FertSiteResponse as FertSiteItem,
  ScheduledDonorResponse as FertScheduledDonorItem,
  TechResponse as TechItem,
  UpsertReverseSortRequest,
  UpsertReverseSortResponse,
} from './types';

export type EditFertilizationRequest = {
  fertilizationId: string;
  transferClient?: number | null;
  frozeClient?: number | null;
  discardClient?: number | null;
  shippedBillable?: number | null;
};

export type EditFertilizationPayload = {
  fertilizationId: string;
  success: boolean;
  message?: string | null;
};

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

function toDateString(date: string | Date): string {
  if (date instanceof Date) {
    return format(date, 'yyyy-MM-dd');
  }
  return date;
}

async function fetchGet<T>(path: string, params?: URLSearchParams): Promise<T> {
  const url = params
    ? `${getBaseUrl()}/api/v1/fertilization/${path}?${params.toString()}`
    : `${getBaseUrl()}/api/v1/fertilization/${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchMutation<T>(
  path: string,
  method: 'POST' | 'PUT',
  body: unknown,
): Promise<T> {
  const url = `${getBaseUrl()}/api/v1/fertilization/${path}`;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(body || `HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchCreatedFerts(params: {
  donorScheduleDate: string;
  fertSiteId: string;
}): Promise<CreatedFertResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorScheduleDate', toDateString(params.donorScheduleDate));
  searchParams.set('fertSiteId', params.fertSiteId);
  return fetchGet<CreatedFertResponse[]>('created-ferts', searchParams);
}

async function fetchPulledSemen(params: {
  donorScheduleDate: string;
  fertSiteId: string;
}): Promise<FertPulledSemenResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorScheduleDate', toDateString(params.donorScheduleDate));
  searchParams.set('fertSiteId', params.fertSiteId);
  return fetchGet<FertPulledSemenResponse[]>('pulled-semen', searchParams);
}

async function fetchDonorSchedules(params: {
  donorScheduleDate: string;
  fertSiteId: string;
}): Promise<FertilizationDonorScheduleResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorScheduleDate', toDateString(params.donorScheduleDate));
  searchParams.set('fertSiteId', params.fertSiteId);
  return fetchGet<FertilizationDonorScheduleResponse[]>(
    'donor-schedules',
    searchParams,
  );
}

async function fetchScheduledDonors(params: {
  donorScheduleDate: string;
  semenId?: string | null;
  accountId?: string | null;
  semenType?: string;
}): Promise<ScheduledDonorResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorScheduleDate', toDateString(params.donorScheduleDate));
  if (params.semenId) searchParams.set('semenId', params.semenId);
  if (params.accountId) searchParams.set('accountId', params.accountId);
  if (params.semenType) searchParams.set('semenType', params.semenType);
  return fetchGet<ScheduledDonorResponse[]>('scheduled-donors', searchParams);
}

async function fetchFertSites(): Promise<FertSiteResponse[]> {
  return fetchGet<FertSiteResponse[]>('fert-sites');
}

async function fetchTechs(params: {
  fertSiteId?: string | null;
}): Promise<TechResponse[]> {
  const searchParams = new URLSearchParams();
  if (params.fertSiteId) searchParams.set('fertSiteId', params.fertSiteId);
  return fetchGet<TechResponse[]>(
    'techs',
    searchParams.toString() ? searchParams : undefined,
  );
}

async function fetchComments(): Promise<CommentResponse[]> {
  return fetchGet<CommentResponse[]>('comments');
}

async function fetchCommentsInGrid(): Promise<CommentResponse[]> {
  return fetchGet<CommentResponse[]>('comments-in-grid');
}

export const createdFertsOptions = (params: {
  donorScheduleDate: string;
  fertSiteId: string;
}) =>
  queryOptions({
    queryKey: ['fertilization', 'created-ferts', params],
    async queryFn() {
      return fetchCreatedFerts(params);
    },
  });

export const fertPulledSemenOptions = (params: {
  donorScheduleDate: string;
  fertSiteId: string;
}) =>
  queryOptions({
    queryKey: ['fertilization', 'pulled-semen', params],
    async queryFn() {
      return fetchPulledSemen(params);
    },
  });

export const fertDonorSchedulesOptions = (params: {
  donorScheduleDate: string;
  fertSiteId: string;
}) =>
  queryOptions({
    queryKey: ['fertilization', 'donor-schedules', params],
    async queryFn() {
      return fetchDonorSchedules(params);
    },
  });

export const fertScheduledDonorsOptions = (params: {
  donorScheduleDate: string;
  semenId?: string | null;
  accountId?: string | null;
  semenType?: string;
}) =>
  queryOptions({
    queryKey: ['fertilization', 'scheduled-donors', params],
    async queryFn() {
      return fetchScheduledDonors(params);
    },
  });

export const fertSitesOptions = () =>
  queryOptions({
    queryKey: ['fertilization', 'fert-sites'],
    async queryFn() {
      return fetchFertSites();
    },
  });

export const fertTechsOptions = (params: { fertSiteId?: string | null }) =>
  queryOptions({
    queryKey: ['fertilization', 'techs', params],
    async queryFn() {
      return fetchTechs(params);
    },
  });

export const fertCommentsOptions = () =>
  queryOptions({
    queryKey: ['fertilization', 'comments'],
    async queryFn() {
      return fetchComments();
    },
  });

export const fertCommentsInGridOptions = () =>
  queryOptions({
    queryKey: ['fertilization', 'comments-in-grid'],
    async queryFn() {
      return fetchCommentsInGrid();
    },
  });

export function useCreateFertilizationRestMutation() {
  return useMutation({
    mutationFn: async (request: CreateFertilizationRequest) => {
      const result = await fetchMutation<CreateFertilizationResponse>(
        '',
        'POST',
        request,
      );
      return { ...result, success: result.success ?? true };
    },
  });
}

export function useUpsertReverseSortRestMutation() {
  return useMutation({
    mutationFn: (request: UpsertReverseSortRequest) =>
      fetchMutation<UpsertReverseSortResponse>('reverse-sort', 'POST', request),
  });
}

export const useEditFertilizationsMutation = () =>
  useMutation({
    mutationFn: async (
      input: EditFertilizationRequest[],
    ): Promise<EditFertilizationPayload[]> => {
      const token = getAccessToken();
      const response = await fetch(`${getBaseUrl()}/api/v1/fertilization`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(input),
      });
      if (!response.ok) throw new Error(await response.text());
      return response.json();
    },
  });
